import {
  Badge,
  Box,
  Button,
  Code,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { MdCheckCircle, MdError, MdWarning } from 'react-icons/md';

const PROVIDERS = {
  1: 'TimeWatch', 2: 'ShiftOrganizer', 3: 'Presto', 5: 'Tizmoon', 6: 'Tabit',
  7: 'Ranadtech', 10: 'Realtime', 11: 'Ovdimnet', 12: 'Electra', 13: 'Dominos',
  14: 'YLM', 15: 'PizzaHut', 16: 'Restigo', 17: 'Renuar', 18: 'Meckano',
  19: 'Dakot', 20: 'Paz', 21: 'Comax', 22: 'Xtras', 23: 'Synerion', 24: 'Haat', 26: 'AvivPos',
};
const providerName = (id) => PROVIDERS[id] ? `${PROVIDERS[id]} (${id})` : `Provider ${id}`;

const STATUS_LABELS = { 100: 'לא פעיל', 200: 'פעיל', 300: 'חסום', 400: 'עזב' };
const BANK_LABELS = { 0: 'None', 1: 'EmployerSubmitted', 2: 'EmployeeConfirmed', 3: 'AdminApproved' };
const SALARY_LABELS = { 1: 'שעתי', 2: 'חודשי' };

function SectionTitle({ children }) {
  return (
    <Text fontWeight="bold" fontSize="sm" color="gray.500" textTransform="uppercase" letterSpacing="wide"
      mt={4} mb={2} pt={4} borderTopWidth="1px" borderColor="gray.200">
      {children}
    </Text>
  );
}

function Row({ label, value, color }) {
  return (
    <Flex justify="space-between" py={1} borderBottomWidth="1px" borderColor="gray.100">
      <Text fontSize="sm" color="gray.500" flexShrink={0} mr={4}>{label}</Text>
      <Text fontSize="sm" fontWeight="medium" color={color || 'inherit'} textAlign="right">{value ?? '—'}</Text>
    </Flex>
  );
}

function IssueList({ issues, warnings, goods }) {
  return (
    <VStack align="stretch" spacing={1}>
      {issues.map((i, idx) => (
        <Flex key={idx} align="flex-start" gap={2}>
          <Box as={MdError} color="red.500" flexShrink={0} mt="2px" size="16px" />
          <Text fontSize="sm" color="red.600">{i}</Text>
        </Flex>
      ))}
      {warnings.map((w, idx) => (
        <Flex key={idx} align="flex-start" gap={2}>
          <Box as={MdWarning} color="orange.400" flexShrink={0} mt="2px" size="16px" />
          <Text fontSize="sm" color="orange.600">{w}</Text>
        </Flex>
      ))}
      {goods.map((g, idx) => (
        <Flex key={idx} align="flex-start" gap={2}>
          <Box as={MdCheckCircle} color="green.500" flexShrink={0} mt="2px" size="16px" />
          <Text fontSize="sm" color="green.700">{g}</Text>
        </Flex>
      ))}
    </VStack>
  );
}

export default function ShiftDebuggerResultsModal({ isOpen, onClose, data }) {
  const bg = useColorModeValue('white', 'gray.800');
  const sectionBg = useColorModeValue('gray.50', 'gray.700');

  if (!data) return null;

  const {
    month, user, employeeDetails, vault, companySettings, salaryDetails,
    integrationActions, recentRuns, pipelineErrors, recentFiles,
    adminTestResult, synerionMismatch, shiftsThisMonth, shiftsByMonth,
    issues, warnings, goods, rootCause,
  } = data;

  const hasIssues = issues?.length > 0;
  const hasWarnings = warnings?.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent bg={bg} maxH="90vh">
        <ModalCloseButton position="absolute" left="8px" right="auto" top="8px" />
        <ModalHeader>
          <Flex align="center" gap={3}>
            <Text>תוצאות בדיקת משמרות</Text>
            {user && <Text fontSize="sm" color="gray.500" fontWeight="normal">— {user.fullName || user.phoneNumber}</Text>}
            <Badge colorScheme={hasIssues ? 'red' : hasWarnings ? 'orange' : 'green'} ml="auto">
              {hasIssues ? `${issues.length} בעיות` : hasWarnings ? `${warnings.length} אזהרות` : 'תקין'}
            </Badge>
          </Flex>
        </ModalHeader>

        <ModalBody>
          <VStack align="stretch" spacing={0}>

            {/* Root Cause */}
            {rootCause && (
              <Box bg={hasIssues ? 'red.50' : 'orange.50'} border="1px" borderColor={hasIssues ? 'red.200' : 'orange.200'} borderRadius="md" p={3} mb={4}>
                <Text fontSize="sm" fontWeight="bold" color={hasIssues ? 'red.700' : 'orange.700'} mb={1}>סיבת שורש</Text>
                <Text fontSize="sm" color={hasIssues ? 'red.600' : 'orange.600'}>{rootCause}</Text>
                {synerionMismatch?.fixSql && (
                  <Code mt={2} display="block" p={2} fontSize="xs" colorScheme="red">{synerionMismatch.fixSql}</Code>
                )}
              </Box>
            )}

            {/* Summary */}
            <Box bg={sectionBg} borderRadius="md" p={3} mb={3}>
              <IssueList issues={issues || []} warnings={warnings || []} goods={goods || []} />
            </Box>

            {/* User */}
            <SectionTitle>1. משתמש</SectionTitle>
            {user ? (
              <Box>
                <Row label="ID" value={user.id} />
                <Row label="שם" value={user.fullName || '—'} />
                <Row label="טלפון" value={user.phoneNumber} />
                <Row label="סטטוס" value={STATUS_LABELS[user.status] || user.status} color={user.status === 200 ? 'green.600' : 'red.600'} />
                <Row label="חברה" value={user.companyId} />
              </Box>
            ) : <Text fontSize="sm" color="red.500">לא נמצא משתמש</Text>}

            {/* Employee Details */}
            {employeeDetails && (
              <>
                <SectionTitle>2. פרטי עובד</SectionTitle>
                <Row label="ת.ז (govId)" value={employeeDetails.govId} />
                <Row label="integrationUid" value={employeeDetails.integrationUid || '—'} />
                <Row label="internalEmployeeId" value={employeeDetails.internalEmployeeId || '—'} />
                <Row label="סטטוס בנק" value={BANK_LABELS[employeeDetails.bankDetailsStatus] || employeeDetails.bankDetailsStatus} />
                <Row label="תאריך סיום" value={employeeDetails.endDate ? String(employeeDetails.endDate).slice(0, 10) : '—'} color={employeeDetails.endDate ? 'orange.500' : 'green.600'} />
                <Row label="סניף" value={employeeDetails.branch || '—'} />
              </>
            )}

            {/* Vault */}
            <SectionTitle>3. אלפון / Phonebook</SectionTitle>
            {vault.error ? (
              <Text fontSize="sm" color="orange.500">{vault.error}</Text>
            ) : (
              <>
                <Row label="עובדים באלפון" value={vault.employeeCount} />
                <Row label="נמצא באלפון" value={vault.found ? '✓ כן' : '✗ לא'} color={vault.found ? 'green.600' : 'red.600'} />
                {vault.matchedRow && (
                  <Box mt={1} p={2} bg="green.50" borderRadius="md" fontSize="xs" fontFamily="mono" whiteSpace="pre-wrap" wordBreak="break-all">
                    {JSON.stringify(vault.matchedRow, null, 2)}
                  </Box>
                )}
              </>
            )}

            {/* Company */}
            {companySettings && (
              <>
                <SectionTitle>4. הגדרות חברה — {companySettings.name}</SectionTitle>
                <Row label="isAttendanceSet" value={companySettings.isAttendanceSet ? '✓' : '✗'} color={companySettings.isAttendanceSet ? 'green.600' : 'red.600'} />
                <Row label="availableToTransferFactor" value={companySettings.availableToTransferFactor} color={Number(companySettings.availableToTransferFactor) === 0 ? 'red.600' : 'inherit'} />
                <Row label="showShiftPayInApp" value={companySettings.showShiftPayInApp ? '✓' : '✗'} />
                <Row label="defaultSalaryAmount" value={companySettings.defaultSalaryAmount ? `₪${companySettings.defaultSalaryAmount}` : '—'} />
                <Row label="transferProvider" value={companySettings.employeesTransferDetailsProvider === 1 ? '1 (Company)' : '2 (Employee)'} />
              </>
            )}

            {/* Salary */}
            <SectionTitle>5. פרטי שכר</SectionTitle>
            {salaryDetails ? (
              <>
                <Row label="סוג שכר" value={SALARY_LABELS[salaryDetails.salaryType] || salaryDetails.salaryType} />
                <Row label="תעריף שעתי" value={salaryDetails.hourlyGrossRate != null ? `₪${salaryDetails.hourlyGrossRate}/ש` : '—'} />
                <Row label="שכר חודשי" value={salaryDetails.monthlyGrossRate != null ? `₪${salaryDetails.monthlyGrossRate}` : '—'} />
                <Row label="פקטור אישי" value={salaryDetails.availableToTransferFactor != null ? salaryDetails.availableToTransferFactor : 'ללא (ירש מחברה)'} color={salaryDetails.availableToTransferFactor != null ? 'orange.500' : 'inherit'} />
              </>
            ) : <Text fontSize="sm" color="gray.400">אין שורת salary_details_entity</Text>}

            {/* Actions */}
            <SectionTitle>6. Integration Actions</SectionTitle>
            {integrationActions?.map((a) => (
              <Row key={a.id} label={`Action #${a.id} — ${a.providerName || providerName(a.providerId)}`} value={a.status === 1 ? '✓ פעיל' : '✗ מושבת'} color={a.status === 1 ? 'green.600' : 'red.600'} />
            ))}

            {/* Recent Runs */}
            <SectionTitle>7. ריצות פייפליין אחרונות</SectionTitle>
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>זמן</Th>
                    <Th>סטטוס</Th>
                    <Th>שורות</Th>
                    <Th>מקור</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {recentRuns?.slice(0, 8).map((r, i) => (
                    <Tr key={i}>
                      <Td fontSize="xs">{r.ts}</Td>
                      <Td><Badge colorScheme={r.status === 'success' ? 'green' : 'red'} fontSize="xs">{r.status}</Badge></Td>
                      <Td fontSize="xs">{r.rows ?? '—'}</Td>
                      <Td fontSize="xs">{r.source ?? '—'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            {/* Pipeline Errors */}
            {pipelineErrors?.length > 0 && (
              <>
                <SectionTitle>8. שגיאות פייפליין לעובד זה</SectionTitle>
                {pipelineErrors.map((e, i) => (
                  <Box key={i} p={2} bg="red.50" borderRadius="md" mb={1}>
                    <Text fontSize="xs" fontWeight="bold">{e.type} [{e.state}]</Text>
                    <Text fontSize="xs" color="gray.600">{e.message?.slice(0, 120)}</Text>
                  </Box>
                ))}
              </>
            )}

            {/* Recent Files */}
            <SectionTitle>9. קבצי אינטגרציה אחרונים</SectionTitle>
            {recentFiles?.slice(0, 5).map((f, i) => (
              <Row key={i} label={`#${f.id} — ${f.fileName}`} value={`${f.ts} ${f.size ? `(${(f.size / 1024).toFixed(0)}KB)` : ''}`} />
            ))}

            {/* Admin Test */}
            <SectionTitle>10. בדיקת ממשקים vs פייפליין</SectionTitle>
            {adminTestResult ? (
              <>
                <Row label="בדיקת ממשקים (לפי טלפון)" value={adminTestResult.count > 0 ? `✓ ${adminTestResult.count} משמרות` : '✗ לא נמצא'} color={adminTestResult.count > 0 ? 'green.600' : 'red.600'} />
                {synerionMismatch && (
                  <>
                    <Row label={`פייפליין (לפי ${synerionMismatch.pipelineKey})`} value={synerionMismatch.byPipeline > 0 ? `✓ ${synerionMismatch.byPipeline} משמרות` : '✗ לא נמצא'} color={synerionMismatch.byPipeline > 0 ? 'green.600' : 'red.600'} />
                    {synerionMismatch.mismatch && (
                      <Box p={2} bg="red.50" borderRadius="md" mt={1}>
                        <Text fontSize="sm" color="red.700" fontWeight="bold">⚠ Synerion employeeId בקובץ: {synerionMismatch.synerionId}</Text>
                        {synerionMismatch.fixSql && (
                          <Code mt={1} display="block" p={2} fontSize="xs">{synerionMismatch.fixSql}</Code>
                        )}
                      </Box>
                    )}
                  </>
                )}
                {adminTestResult.shifts?.length > 0 && (
                  <Box mt={2} overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead><Tr><Th>תאריך</Th><Th>שעות</Th></Tr></Thead>
                      <Tbody>
                        {adminTestResult.shifts.map((s, i) => (
                          <Tr key={i}><Td fontSize="xs">{s.date}</Td><Td fontSize="xs">{Number(s.hours).toFixed(2)}h</Td></Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </>
            ) : <Text fontSize="sm" color="gray.400">לא רץ (לא נמצא משתמש)</Text>}

            {/* Shifts This Month */}
            <SectionTitle>11. משמרות בחודש {month}</SectionTitle>
            {shiftsThisMonth ? (
              <>
                <Row label="מספר משמרות" value={Number(shiftsThisMonth.cnt)} color={Number(shiftsThisMonth.cnt) > 0 ? 'green.600' : 'red.600'} />
                <Row label="סה״כ שעות" value={shiftsThisMonth.hrs ? `${Number(shiftsThisMonth.hrs).toFixed(1)}h` : '—'} />
                <Row label="טווח תאריכים"
                  value={shiftsThisMonth.first
                    ? `${String(shiftsThisMonth.first).slice(0, 10)} → ${String(shiftsThisMonth.last).slice(0, 10)}`
                    : '—'} />
              </>
            ) : <Text fontSize="sm" color="gray.400">לא נמצא משתמש</Text>}

            {/* Shifts By Month */}
            <SectionTitle>12. משמרות לפי חודש (4 חודשים אחרונים)</SectionTitle>
            {shiftsByMonth?.length > 0 ? (
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead><Tr><Th>חודש</Th><Th>משמרות</Th><Th>שעות</Th><Th>פרוטוקול</Th></Tr></Thead>
                  <Tbody>
                    {shiftsByMonth.map((r, i) => (
                      <Tr key={i}>
                        <Td fontSize="xs">{r.mon}</Td>
                        <Td fontSize="xs">{r.cnt}</Td>
                        <Td fontSize="xs">{Number(r.hrs).toFixed(1)}h</Td>
                        <Td fontSize="xs">{providerName(r.integrationProviderId)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : <Text fontSize="sm" color="gray.400">אין משמרות ב-4 חודשים האחרונים</Text>}

          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>סגור</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
